import '../style/main.scss';

// Temporary script content as requested
console.log(1);

import cvReadyPromise from "@techstark/opencv-js";

// Configurable constants
const N_FOUND_CONFIRM = 3;
const M_LOST_CONFIRM = 6;
const SMOOTH_ALPHA = 0.25;
const APPROX_EPS_K = 0.02;
const AREA_RATIO_MIN = 0.06;
const ANGLE_TOL_DEG = 20;
const ASPECT_MIN = 0.6;
const ASPECT_MAX = 1.8;
const UI_UPDATE_INTERVAL_MS = 120;
const CANNY_LOW = 75;
const CANNY_HIGH = 200;
const BLUR_SIZE = 5;

const statusEl = document.getElementById('status');
const videoEl = document.getElementById('camera');
const canvasEl = document.getElementById('output');
const ctx = canvasEl.getContext('2d');

function setStatus(msg) {
  if (statusEl) statusEl.textContent = msg;
}

async function setupCamera() {
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    throw new Error('getUserMedia не поддерживается в этом браузере.');
  }
  const constraints = {
    video: {
      facingMode: { ideal: 'environment' },
      width: { ideal: 1280 },
      height: { ideal: 720 }
    },
    audio: false
  };
  const stream = await navigator.mediaDevices.getUserMedia(constraints);
  videoEl.srcObject = stream;
  await videoEl.play();
  if (videoEl.readyState >= 2) {
    return stream;
  }
  await new Promise(resolve => {
    videoEl.onloadedmetadata = () => resolve();
  });
  return stream;
}

function drawPolygon(ctx, points) {
  if (!points || points.length < 4) return;
  ctx.save();
  ctx.lineWidth = 4;
  ctx.strokeStyle = 'lime';
  ctx.fillStyle = 'rgba(0,255,0,0.15)';
  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].y);
  for (let i = 1; i < points.length; i++) ctx.lineTo(points[i].x, points[i].y);
  ctx.closePath();
  ctx.stroke();
  ctx.fill();
  ctx.restore();
}

// Helper: hysteresis streaks
function updateStreaks(found, state) {
  let { foundStreak, lostStreak, confirmedFound } = state;
  let changed = false;
  if (found) {
    foundStreak += 1;
    lostStreak = 0;
    if (!confirmedFound && foundStreak >= N_FOUND_CONFIRM) {
      confirmedFound = true;
      changed = true;
    }
  } else {
    lostStreak += 1;
    foundStreak = 0;
    if (confirmedFound && lostStreak >= M_LOST_CONFIRM) {
      confirmedFound = false;
      changed = true;
    }
  }
  return { foundStreak, lostStreak, confirmedFound, changed };
}

// Helper: EMA smoothing for polygon points
function smoothPoints(prevPoints, newPoints, alpha) {
  if (!newPoints || newPoints.length !== 4) return prevPoints || null;
  if (!prevPoints || prevPoints.length !== 4) return newPoints.map(p => ({ x: p.x, y: p.y }));
  const out = [];
  for (let i = 0; i < 4; i++) {
    const px = prevPoints[i].x;
    const py = prevPoints[i].y;
    const nx = newPoints[i].x;
    const ny = newPoints[i].y;
    out.push({ x: (1 - alpha) * px + alpha * nx, y: (1 - alpha) * py + alpha * ny });
  }
  return out;
}

// Helper: check rectangle-like quadrilateral
function isRectLike(points, angleTolDeg, aspectMin, aspectMax) {
  if (!points || points.length !== 4) return false;
  // angle check via cosine
  const cosTol = Math.cos((90 - angleTolDeg) * Math.PI / 180);
  for (let i = 0; i < 4; i++) {
    const p0 = points[(i + 3) % 4];
    const p1 = points[i];
    const p2 = points[(i + 1) % 4];
    const v1x = p0.x - p1.x, v1y = p0.y - p1.y;
    const v2x = p2.x - p1.x, v2y = p2.y - p1.y;
    const n1 = Math.hypot(v1x, v1y);
    const n2 = Math.hypot(v2x, v2y);
    if (n1 === 0 || n2 === 0) return false;
    const cosA = (v1x * v2x + v1y * v2y) / (n1 * n2);
    if (Math.abs(cosA) > cosTol) return false;
  }
  // aspect ratio using bounding box
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  const w = maxX - minX;
  const h = maxY - minY;
  if (w <= 0 || h <= 0) return false;
  const ratio = w / h;
  return ratio >= aspectMin && ratio <= aspectMax;
}

// Helper: order quad points consistently TL, TR, BR, BL
function orderQuadPoints(points) {
  if (!points || points.length !== 4) return points;
  // Compute sums and diffs
  const sums = points.map(p => p.x + p.y);
  const diffs = points.map(p => p.x - p.y);
  const tlIdx = sums.indexOf(Math.min(...sums));
  const brIdx = sums.indexOf(Math.max(...sums));
  const trIdx = diffs.indexOf(Math.max(...diffs));
  const blIdx = diffs.indexOf(Math.min(...diffs));
  const ordered = [points[tlIdx], points[trIdx], points[brIdx], points[blIdx]];
  return ordered;
}

function detectPaper(src, cv) {
  const w = src.cols;
  const h = src.rows;
  let gray = new cv.Mat();
  let blur = new cv.Mat();
  let edges = new cv.Mat();
  let contours = new cv.MatVector();
  let hierarchy = new cv.Mat();
  let bestApprox = null;
  let bestRatio = 0;

  try {
    cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY);
    cv.GaussianBlur(gray, blur, new cv.Size(BLUR_SIZE, BLUR_SIZE), 0);
    cv.Canny(blur, edges, CANNY_LOW, CANNY_HIGH);

    cv.findContours(edges, contours, hierarchy, cv.RETR_EXTERNAL, cv.CHAIN_APPROX_SIMPLE);
    for (let i = 0; i < contours.size(); i++) {
      const cnt = contours.get(i);
      const peri = cv.arcLength(cnt, true);
      const approx = new cv.Mat();
      cv.approxPolyDP(cnt, approx, APPROX_EPS_K * peri, true);

      if (approx.rows === 4 && cv.isContourConvex(approx)) {
        const area = Math.abs(cv.contourArea(approx));
        const ratio = area / (w * h);
        if (ratio >= AREA_RATIO_MIN && ratio > bestRatio) {
          if (bestApprox) bestApprox.delete();
          bestApprox = approx;
          bestRatio = ratio;
        } else {
          approx.delete();
        }
      } else {
        approx.delete();
      }
      cnt.delete();
    }

    if (bestApprox) {
      const data = bestApprox.data32S;
      const pts = [];
      for (let i = 0; i < data.length; i += 2) {
        pts.push({ x: data[i], y: data[i + 1] });
      }
      const ordered = orderQuadPoints(pts);
      // Apply rectangle-like filter only to the best candidate
      if (isRectLike(ordered, ANGLE_TOL_DEG, ASPECT_MIN, ASPECT_MAX)) {
        return { found: true, points: ordered };
      }
    }
    return { found: false };
  } finally {
    gray.delete(); blur.delete(); edges.delete(); contours.delete(); hierarchy.delete();
    if (bestApprox) { bestApprox.delete(); }
  }
}

async function main() {
  try {
    setStatus('Загрузка OpenCV...');
    const cv = await cvReadyPromise;
    setStatus('OpenCV готов. Запрашиваем камеру...');
    const stream = await setupCamera();

    // Set canvas size to video size
    const w = videoEl.videoWidth || 640;
    const h = videoEl.videoHeight || 480;
    canvasEl.width = w;
    canvasEl.height = h;

    setStatus('Камера запущена. Идёт анализ...');

    // Hysteresis, smoothing, and capture state
    let foundStreak = 0;
    let lostStreak = 0;
    let confirmedFound = false;
    let smoothedPoints = null;
    let lastUiUpdateTime = 0;
    let lastStatusText = 'Камера запущена. Идёт анализ...';
    let bestCandidate = null; // { sharpness, points, imageData }
    let captured = false;

    const tick = () => {
      if (captured) return;

      // Draw current frame
      ctx.drawImage(videoEl, 0, 0, w, h);
      // Get image data and create Mat
      const imageData = ctx.getImageData(0, 0, w, h);
      const src = cv.matFromImageData(imageData);

      const result = detectPaper(src, cv);

      // Track best candidate across the current found run
      const prevFoundStreak = foundStreak;
      if (result.found) {
        if (prevFoundStreak === 0) {
          bestCandidate = null;
        }
        let sharpness = 0;
        try {
          sharpness = computeSharpnessVariance(cv, src);
        } catch (e) {
          sharpness = 0;
        }
        if (!bestCandidate || sharpness > bestCandidate.sharpness) {
          bestCandidate = { sharpness, points: result.points, imageData };
        }
        smoothedPoints = smoothPoints(smoothedPoints, result.points, SMOOTH_ALPHA);
      }

      // src is no longer needed after sharpness calc
      src.delete();

      // Update hysteresis
      const upd = updateStreaks(result.found, { foundStreak, lostStreak, confirmedFound });
      foundStreak = upd.foundStreak;
      lostStreak = upd.lostStreak;
      const statusChanged = upd.changed;
      confirmedFound = upd.confirmedFound;

      // On confirmation, capture and warp best candidate to fill canvas
      if (statusChanged && confirmedFound && bestCandidate && bestCandidate.points) {
        try {
          warpAndDrawToCanvas(cv, bestCandidate.imageData, bestCandidate.points, canvasEl);
          setStatus('Лист бумаги: найден');
          if (stream && stream.getTracks) {
            stream.getTracks().forEach(t => t.stop && t.stop());
          }
          captured = true;
          return; // stop processing further frames
        } catch (e) {
          console.error('Capture/warp error:', e);
        }
      }

      // Throttle UI updates
      const now = performance.now();
      if (now - lastUiUpdateTime >= UI_UPDATE_INTERVAL_MS) {
        if (confirmedFound) {
          if (smoothedPoints && smoothedPoints.length === 4) {
            drawPolygon(ctx, smoothedPoints);
          }
          if (lastStatusText !== 'Лист бумаги: найден') {
            setStatus('Лист бумаги: найден');
            lastStatusText = 'Лист бумаги: найден';
          }
        } else {
          // Not found: only status text, polygon will not be drawn (frame redraw clears it)
          if (lastStatusText !== 'Лист бумаги: не найден') {
            setStatus('Лист бумаги: не найден');
            lastStatusText = 'Лист бумаги: не найден';
          }
        }
        lastUiUpdateTime = now;
      }

      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);

  } catch (err) {
    console.error(err);
    setStatus('Ошибка: ' + err.message);
  }
}

main();

// Sharpness metric: variance of Laplacian (higher = sharper)
function computeSharpnessVariance(cv, srcRgbaMat) {
  let gray = new cv.Mat();
  let lap = new cv.Mat();
  let mean = new cv.Mat();
  let stddev = new cv.Mat();
  try {
    cv.cvtColor(srcRgbaMat, gray, cv.COLOR_RGBA2GRAY);
    cv.Laplacian(gray, lap, cv.CV_64F);
    cv.meanStdDev(lap, mean, stddev);
    const s = stddev.data64F ? stddev.data64F[0] : (stddev.doubleAt ? stddev.doubleAt(0, 0) : 0);
    return s * s;
  } finally {
    gray.delete();
    lap.delete();
    mean.delete();
    stddev.delete();
  }
}

// Perspective warp from detected quad to full canvas
function warpAndDrawToCanvas(cv, imageData, srcPoints, canvas) {
  const src = cv.matFromImageData(imageData);
  const dstW = canvas.width;
  const dstH = canvas.height;
  const srcArr = [
    srcPoints[0].x, srcPoints[0].y,
    srcPoints[1].x, srcPoints[1].y,
    srcPoints[2].x, srcPoints[2].y,
    srcPoints[3].x, srcPoints[3].y,
  ];
  const dstArr = [
    0, 0,
    dstW - 1, 0,
    dstW - 1, dstH - 1,
    0, dstH - 1,
  ];
  const srcMat = cv.matFromArray(4, 1, cv.CV_32FC2, srcArr);
  const dstMat = cv.matFromArray(4, 1, cv.CV_32FC2, dstArr);
  const M = cv.getPerspectiveTransform(srcMat, dstMat);
  const dst = new cv.Mat();
  try {
    cv.warpPerspective(src, dst, M, new cv.Size(dstW, dstH), cv.INTER_LINEAR, cv.BORDER_REPLICATE, new cv.Scalar());
    cv.imshow(canvas, dst);
  } finally {
    src.delete();
    srcMat.delete();
    dstMat.delete();
    M.delete();
    dst.delete();
  }
}
